'use client'

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Megaphone, Plus, Radio, Upload, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import Turnstile, { type TurnstileHandle } from '../../Turnstile'
import { CttPreview } from './CttPreview'
import { fmtDatePt } from './cttUtils'
import type { ProcessedTheme, BrowserRow } from '../../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses, compactDate, postTypeLabel, postTypeStyle } from '../../ProcessedEntry/DomainEntry/processedTypes'
import { CampField } from '../../ProcessedEntry/DomainEntry/sharedComponents'
import type { CttCampaign, CttCampaignImage, RawEntry, SelectedRow, PreviewEntry } from '../trustCenterTypes'

const TURNSTILE_SITEKEY = process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_TF_CENTER_GETENTRIES_KEY ?? ''

export function CttDetailPanel({
  selectedRow,
  theme,
  browserRows,
  browserRowsLoading,
  onClose,
  onSaved,
}: {
  selectedRow: SelectedRow
  theme: ProcessedTheme
  browserRows: BrowserRow[]
  browserRowsLoading: boolean
  onClose: () => void
  onSaved: () => void
}) {
  const isDark = theme === 'dark'
  const c = watchClasses(theme)
  const border  = 'border-border'
  const softBg  = 'bg-secondary'
  const label   = 'text-muted-foreground'
  const muted   = 'text-muted-foreground'
  const soft    = 'text-muted-foreground'

  const isBrand = selectedRow.kind === 'brand'

  // ── Form state ──────────────────────────────────────────────────────────────
  const [editHeadline, setEditHeadline] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveResult, setSaveResult] = useState<{ ok: boolean; msg: string } | null>(null)

  // Promote (processed → brand)
  const [promoteImage, setPromoteImage] = useState<{ base64: string; mime: string } | null>(null)
  const [selectedR2Key, setSelectedR2Key] = useState<string | null>(null)
  const [imageMode, setImageMode] = useState<'screenshot' | 'upload'>('screenshot')

  // Brand image management
  const [brandImages, setBrandImages] = useState<CttCampaignImage[]>([])
  const [brandAddOpen, setBrandAddOpen] = useState(false)
  const [brandAddMode, setBrandAddMode] = useState<'screenshot' | 'upload'>('screenshot')
  const [brandAddImage, setBrandAddImage] = useState<{ base64: string; mime: string } | null>(null)
  const [brandAddR2Key, setBrandAddR2Key] = useState<string | null>(null)
  const [addingImage, setAddingImage] = useState(false)
  const [removingR2Key, setRemovingR2Key] = useState<string | null>(null)
  const [loadingR2Key, setLoadingR2Key] = useState<string | null>(null)

  // ── Turnstile ───────────────────────────────────────────────────────────────
  const turnstileRef = useRef<TurnstileHandle>(null)
  const pendingTokenRef = useRef<((token: string) => void) | null>(null)

  const handleVerify = useCallback((token: string) => {
    pendingTokenRef.current?.(token)
    pendingTokenRef.current = null
  }, [])

  const getToken = useCallback((): Promise<string> => {
    return new Promise((resolve) => {
      pendingTokenRef.current = resolve
      turnstileRef.current?.reset()
      turnstileRef.current?.run()
    })
  }, [])

  // ── Sync form when selection changes ────────────────────────────────────────
  useEffect(() => {
    setSaveResult(null)
    setPromoteImage(null)
    setSelectedR2Key(null)
    setImageMode('screenshot')
    setBrandAddOpen(false)
    setBrandAddImage(null)
    setBrandAddR2Key(null)
    setBrandAddMode('screenshot')

    if (selectedRow.kind === 'brand') {
      const e = selectedRow.data as CttCampaign
      setEditHeadline(e.headline)
      setEditDescription(e.description)
      setBrandImages(e.images)
    } else {
      const e = selectedRow.data as RawEntry
      setEditHeadline(e.headline ?? '')
      setEditDescription(e.message_content ?? '')
      setBrandImages([])
    }
  }, [selectedRow])

  // ── Preview ─────────────────────────────────────────────────────────────────
  const previewEntry = useMemo((): PreviewEntry => {
    if (selectedRow.kind === 'brand') {
      const e = selectedRow.data as CttCampaign
      const paragraphs = editDescription.split(/\n\n+/).map((p) => p.trim()).filter(Boolean)
      return {
        date: fmtDatePt(e.created_at),
        title: editHeadline || e.headline,
        paragraphs: paragraphs.length ? paragraphs : ['Sem detalhes disponíveis.'],
        images: e.images.map((i) => i.url),
      }
    } else {
      const e = selectedRow.data as RawEntry
      const parts: string[] = []
      if (e.sender) parts.push(`Remetente: ${e.sender}`)
      if (editDescription) parts.push(editDescription)
      return {
        date: fmtDatePt(e.created_at),
        title: editHeadline || e.headline || 'Denúncia',
        paragraphs: parts.length ? parts : ['Sem detalhes disponíveis.'],
        images: [],
      }
    }
  }, [selectedRow, editHeadline, editDescription])

  // ── Screenshot picker ────────────────────────────────────────────────────────
  const handleSelectScreenshot = useCallback(async (
    r2Key: string,
    setImage: (img: { base64: string; mime: string }) => void,
    setKey: (k: string) => void,
  ) => {
    if (loadingR2Key) return
    setLoadingR2Key(r2Key)
    try {
      const token = await getToken()
      const res = await fetch('/api/screenshots/bykey', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ r2Key, turnstileToken: token }),
      })
      const json = await res.json()
      if (!json.success) { setSaveResult({ ok: false, msg: 'Failed to load screenshot.' }); return }
      const scenarios = json.data?.data?.buffer?.scenarios
      const base64 = scenarios?.desktop?.screenshots?.[0]?.data ?? scenarios?.mobile?.screenshots?.[0]?.data
      if (!base64) { setSaveResult({ ok: false, msg: 'No image data in this capture.' }); return }
      setImage({ base64, mime: 'image/png' })
      setKey(r2Key)
      setSaveResult(null)
    } catch {
      setSaveResult({ ok: false, msg: 'Network error loading screenshot.' })
    } finally {
      setLoadingR2Key(null)
    }
  }, [getToken, loadingR2Key])

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleSaveBrand = async () => {
    if (selectedRow.kind !== 'brand' || saving) return
    setSaving(true)
    setSaveResult(null)
    try {
      const res = await fetch(`/api/campaigns/ctt/${selectedRow.data.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ headline: editHeadline, description: editDescription }),
      })
      const payload = await res.json()
      if (payload.success) {
        setSaveResult({ ok: true, msg: 'Campaign updated.' })
        onSaved()
      } else {
        setSaveResult({ ok: false, msg: payload.error ?? 'Failed to update.' })
      }
    } catch {
      setSaveResult({ ok: false, msg: 'Network error.' })
    } finally {
      setSaving(false)
    }
  }

  const handlePromote = async () => {
    if (selectedRow.kind !== 'processed' || saving) return
    setSaving(true)
    setSaveResult(null)
    try {
      const res = await fetch('/api/campaigns/ctt/promote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          processedEntryId: selectedRow.data.id,
          headline: editHeadline,
          description: editDescription,
          ...(promoteImage && { imageBase64: promoteImage.base64, imageMimeType: promoteImage.mime, imageAltText: editHeadline }),
        }),
      })
      const payload = await res.json()
      if (payload.success) {
        setSaveResult({ ok: true, msg: 'Brand campaign created.' })
        onSaved()
        onClose()
      } else {
        setSaveResult({ ok: false, msg: payload.error ?? 'Failed to create campaign.' })
      }
    } catch {
      setSaveResult({ ok: false, msg: 'Network error.' })
    } finally {
      setSaving(false)
    }
  }

  const handleAddImage = async () => {
    if (selectedRow.kind !== 'brand' || !brandAddImage || addingImage) return
    setAddingImage(true)
    try {
      const res = await fetch(`/api/campaigns/ctt/${selectedRow.data.id}/images`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageBase64: brandAddImage.base64, imageMimeType: brandAddImage.mime, imageAltText: editHeadline }),
      })
      const payload = await res.json()
      if (payload.success) {
        setBrandImages((prev) => [...prev, payload.data])
        setBrandAddOpen(false)
        setBrandAddImage(null)
        setBrandAddR2Key(null)
        setSaveResult({ ok: true, msg: 'Image added.' })
      } else {
        setSaveResult({ ok: false, msg: payload.error ?? 'Failed to add image.' })
      }
    } catch {
      setSaveResult({ ok: false, msg: 'Network error.' })
    } finally {
      setAddingImage(false)
    }
  }

  const handleRemoveImage = async (r2Key: string) => {
    if (selectedRow.kind !== 'brand' || removingR2Key) return
    setRemovingR2Key(r2Key)
    setBrandImages((prev) => prev.filter((img) => img.r2_key !== r2Key))
    try {
      const res = await fetch(`/api/campaigns/ctt/${selectedRow.data.id}/images`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ r2Key }),
      })
      const payload = await res.json()
      if (!payload.success) setSaveResult({ ok: false, msg: payload.error ?? 'Failed to remove image.' })
    } catch {
      setSaveResult({ ok: false, msg: 'Network error.' })
    } finally {
      setRemovingR2Key(null)
    }
  }

  const entry = selectedRow.data as RawEntry
  const entryTitle = !isBrand ? (entry.headline ?? entry.message_content?.slice(0, 80) ?? '—') : ''

  return (
    <div className="flex flex-col overflow-y-auto h-full">
      <Turnstile ref={turnstileRef} sitekey={TURNSTILE_SITEKEY} onVerify={handleVerify} />

      {isBrand ? (
        /* ── Brand: compact header ─────────────────────────────────────── */
        <>
          <div className={`flex items-center gap-2 px-4 py-2.5 border-b shrink-0 ${border} ${softBg}`}>
            <span className="font-mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 bg-signal-surface border-signal/30 text-signal">Brand</span>
            <span className={`font-mono text-[10px] uppercase tracking-widest truncate flex-1 ${muted}`}>
              {(selectedRow.data as CttCampaign).headline}
            </span>
            <button onClick={onClose} className={`flex items-center justify-center h-6 w-6 border transition-colors shrink-0 hover:text-foreground ${border} ${muted}`}>
              <X className="h-3 w-3" />
            </button>
          </div>
          <div className={`border-b py-2 shrink-0 ${border}`}>
            <CttPreview entry={previewEntry} tab="Brand campaigns" />
          </div>
        </>
      ) : (
        /* ── Processed: ProcessedEntryCard-style header ────────────────── */
        <div className={`relative border-b px-6 pt-6 pb-5 shrink-0 ${border}`}>
          <button
            onClick={onClose}
            className={`absolute top-3.5 right-3.5 flex items-center justify-center h-6 w-6 border transition-colors hover:text-foreground ${border} ${muted}`}
          >
            <X className="h-3 w-3" />
          </button>

          {/* Chips row */}
          <div className="flex flex-wrap items-center gap-2 pr-8">
            <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 ${c.line} ${c.soft}`}>
              <span className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>Entry</span>
              <span className={`font-mono text-[10px] ${c.muted}`}>{entry.id.slice(0, 8)}…</span>
            </span>
            <span className={`font-mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 bg-warning-surface border-warning-border text-warning`}>
              Processed
            </span>
            <span className={`text-xs ${c.muted}`}>first seen {compactDate(entry.created_at)}</span>
          </div>

          {/* Title */}
          <h2 className={`mt-4 text-xl font-semibold leading-snug tracking-[-0.01em] ${c.text}`}>
            "{entryTitle}"
          </h2>

          {/* Message body */}
          {entry.message_content && entry.headline && (
            <p className={`mt-3 text-[17px] leading-7 ${c.muted}`}>
              {entry.message_content}
            </p>
          )}

          {/* Pills */}
          <div className="mt-4 flex flex-wrap gap-2">
            {entry.post_type && (
              <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${c.line} ${c.soft}`}>
                <Radio className="h-3 w-3" />
                {postTypeLabel(entry.post_type)}{entry.sender ? ` · ${entry.sender}` : ''}
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── Form section ──────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-4 px-5 py-4">
        <div className={`font-mono text-[10px] uppercase tracking-widest ${muted}`}>
          {isBrand ? '// Edit brand campaign' : '// Promote to brand campaign'}
        </div>

        {/* Live preview (processed only — brand shows it above) */}
        {!isBrand && (
          <div className={`border-b pb-2 -mx-5 px-5 ${border}`}>
            <CttPreview entry={previewEntry} tab="Processed entries" />
          </div>
        )}

        <CampField label="Headline">
          <input
            value={editHeadline}
            onChange={(e) => setEditHeadline(e.target.value)}
            className="w-full h-8 border border-input rounded-md px-3 text-[13px] bg-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            placeholder="Public-facing headline…"
          />
        </CampField>
        <CampField label="Description">
          <textarea
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            rows={5}
            className="w-full border border-input rounded-md px-3 py-2 text-[13px] font-sans resize-y bg-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            placeholder="Public-facing description…"
          />
        </CampField>

        {isBrand ? (
          /* ── Brand image management ─────────────────────────────────────── */
          <div>
            <div className={`font-mono text-[9px] uppercase tracking-widest mb-2 ${muted}`}>
              Images · {brandImages.length}
            </div>
            {brandImages.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {brandImages.map((img) => (
                  <div key={img.r2_key} className="relative group">
                    <img
                      src={img.url}
                      alt={img.alt_text || ''}
                      className={`h-20 w-auto rounded border object-contain ${border} ${softBg}`}
                    />
                    <button
                      onClick={() => handleRemoveImage(img.r2_key)}
                      disabled={removingR2Key === img.r2_key}
                      className="absolute -top-1.5 -right-1.5 flex items-center justify-center h-4 w-4 rounded-full bg-destructive text-white opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-40"
                      title="Remove image"
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {brandImages.length === 0 && (
              <p className={`font-mono text-[10px] mb-3 ${muted}`}>No images attached.</p>
            )}

            <button
              onClick={() => { setBrandAddOpen((o) => !o); setBrandAddImage(null); setBrandAddR2Key(null) }}
              className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-signal hover:text-signal border border-signal/30 hover:border-signal px-2.5 py-1 transition-colors"
            >
              <Plus className="h-3 w-3" />
              {brandAddOpen ? 'Cancel' : 'Add image'}
            </button>

            {brandAddOpen && (
              <div className={`mt-3 space-y-3 border rounded p-3 ${border}`}>
                <div className="flex gap-1">
                  {(['screenshot', 'upload'] as const).map((mode) => (
                    <button
                      key={mode}
                      onClick={() => { setBrandAddMode(mode); setBrandAddImage(null); setBrandAddR2Key(null) }}
                      className={`font-mono text-[9px] uppercase tracking-widest border px-2.5 py-1 transition-colors ${
                        brandAddMode === mode
                          ? 'bg-primary text-primary-foreground border-signal/30 text-white'
                          : `${border} ${label} hover:border-signal hover:text-signal`
                      }`}
                    >
                      {mode === 'screenshot' ? 'From screenshots' : 'Upload image'}
                    </button>
                  ))}
                </div>

                {brandAddMode === 'screenshot' && (
                  <div>
                    {browserRowsLoading ? (
                      <p className={`font-mono text-[10px] animate-pulse ${muted}`}>Loading screenshots…</p>
                    ) : browserRows.length === 0 ? (
                      <p className={`font-mono text-[10px] ${muted}`}>No screenshots available.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {browserRows.map((r) => {
                          const isSel = brandAddR2Key === r.r2Key
                          const isLoad = loadingR2Key === r.r2Key
                          return (
                            <button
                              key={r.id}
                              onClick={() => handleSelectScreenshot(r.r2Key!, (img) => setBrandAddImage(img), (k) => setBrandAddR2Key(k))}
                              disabled={!!loadingR2Key}
                              className={`flex flex-col items-center gap-0.5 rounded border px-2.5 py-1.5 transition-colors disabled:opacity-50 ${
                                isSel
                                  ? 'border-signal bg-signal-surface text-signal'
                                  : `${border} ${label} hover:border-signal hover:bg-secondary`
                              }`}
                            >
                              <span className="font-mono text-[8px]">
                                {fmtDatePt(r.createdAt)}
                              </span>
                              <span className="font-mono text-[8px]">
                                {isLoad ? '⟳ loading…' : isSel ? '✓ selected' : 'select'}
                              </span>
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )}

                {brandAddMode === 'upload' && (
                  <label className="block cursor-pointer">
                    <div
                      className={`flex flex-col items-center gap-2 rounded border-2 border-dashed px-4 py-4 text-center transition-colors hover:border-signal hover:bg-secondary border-border`}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault()
                        const file = e.dataTransfer.files[0]
                        if (!file?.type.startsWith('image/')) return
                        const reader = new FileReader()
                        reader.onload = (ev) => {
                          const dataUrl = ev.target?.result as string
                          const [header, base64] = dataUrl.split(',')
                          const mime = header.match(/data:([^;]+)/)?.[1] ?? 'image/png'
                          setBrandAddImage({ base64, mime })
                          setBrandAddR2Key(null)
                        }
                        reader.readAsDataURL(file)
                      }}
                    >
                      <Upload className={`h-4 w-4 ${muted}`} />
                      <p className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>
                        {brandAddImage && !brandAddR2Key ? 'Image loaded ✓ · click to replace' : 'Click or drag to upload'}
                      </p>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (!file) return
                        const reader = new FileReader()
                        reader.onload = (ev) => {
                          const dataUrl = ev.target?.result as string
                          const [header, base64] = dataUrl.split(',')
                          const mime = header.match(/data:([^;]+)/)?.[1] ?? 'image/png'
                          setBrandAddImage({ base64, mime })
                          setBrandAddR2Key(null)
                        }
                        reader.readAsDataURL(file)
                      }}
                    />
                  </label>
                )}

                {brandAddImage && (
                  <div className="flex items-start gap-2">
                    <img
                      src={`data:${brandAddImage.mime};base64,${brandAddImage.base64}`}
                      alt="Preview"
                      className={`max-h-16 w-auto rounded border object-contain ${border} ${softBg}`}
                    />
                    <button
                      onClick={() => { setBrandAddImage(null); setBrandAddR2Key(null) }}
                      className="mt-0.5 text-muted-foreground hover:text-destructive transition-colors"
                      title="Remove"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}

                <Button
                  size="sm"
                  className="font-mono text-[10px] uppercase tracking-widest gap-1.5 w-full"
                  disabled={!brandAddImage || addingImage}
                  onClick={handleAddImage}
                >
                  {addingImage ? 'Adding…' : 'Add image'}
                </Button>
              </div>
            )}
          </div>
        ) : (
          /* ── Promote: campaign image picker ─────────────────────────────── */
          <div>
            <div className={`font-mono text-[9px] uppercase tracking-widest mb-2 ${muted}`}>
              Campaign image <span className={'text-subtle'}>(optional)</span>
            </div>

            <div className="flex gap-1 mb-3">
              {(['screenshot', 'upload'] as const).map((mode) => (
                <button
                  key={mode}
                  onClick={() => { setImageMode(mode); setPromoteImage(null); setSelectedR2Key(null) }}
                  className={`font-mono text-[9px] uppercase tracking-widest border px-2.5 py-1 transition-colors ${
                    imageMode === mode
                      ? 'bg-primary text-primary-foreground border-signal/30 text-white'
                      : `${border} ${label} hover:border-signal hover:text-signal`
                  }`}
                >
                  {mode === 'screenshot' ? 'From screenshots' : 'Upload image'}
                </button>
              ))}
            </div>

            {imageMode === 'screenshot' && (
              <div>
                {browserRowsLoading ? (
                  <p className={`font-mono text-[10px] animate-pulse ${muted}`}>Loading screenshots…</p>
                ) : browserRows.length === 0 ? (
                  <p className={`font-mono text-[10px] ${muted}`}>
                    {(selectedRow.data as RawEntry).user_entry_domain_id
                      ? 'No screenshots available for this entry.'
                      : 'No domain associated — no screenshots available.'}
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {browserRows.map((r) => {
                      const isSel = selectedR2Key === r.r2Key
                      const isLoad = loadingR2Key === r.r2Key
                      return (
                        <button
                          key={r.id}
                          onClick={() => handleSelectScreenshot(r.r2Key!, (img) => setPromoteImage(img), (k) => setSelectedR2Key(k))}
                          disabled={!!loadingR2Key}
                          className={`flex flex-col items-center gap-0.5 rounded border px-2.5 py-1.5 transition-colors disabled:opacity-50 ${
                            isSel
                              ? 'border-signal bg-signal-surface text-signal'
                              : `${border} ${label} hover:border-signal hover:bg-secondary`
                          }`}
                        >
                          <span className="font-mono text-[8px]">
                            {fmtDatePt(r.createdAt)}
                          </span>
                          <span className="font-mono text-[8px]">
                            {isLoad ? '⟳ loading…' : isSel ? '✓ selected' : 'select'}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )}

            {imageMode === 'upload' && (
              <label className="block cursor-pointer">
                <div
                  className={`flex flex-col items-center gap-2 rounded border-2 border-dashed px-4 py-4 text-center transition-colors hover:border-signal hover:bg-secondary border-border`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault()
                    const file = e.dataTransfer.files[0]
                    if (!file?.type.startsWith('image/')) return
                    const reader = new FileReader()
                    reader.onload = (ev) => {
                      const dataUrl = ev.target?.result as string
                      const [header, base64] = dataUrl.split(',')
                      const mime = header.match(/data:([^;]+)/)?.[1] ?? 'image/png'
                      setPromoteImage({ base64, mime })
                    }
                    reader.readAsDataURL(file)
                  }}
                >
                  <Upload className={`h-4 w-4 ${muted}`} />
                  <p className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>
                    {promoteImage && !selectedR2Key ? 'Image loaded ✓ · click to replace' : 'Click or drag to upload'}
                  </p>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (!file) return
                    const reader = new FileReader()
                    reader.onload = (ev) => {
                      const dataUrl = ev.target?.result as string
                      const [header, base64] = dataUrl.split(',')
                      const mime = header.match(/data:([^;]+)/)?.[1] ?? 'image/png'
                      setPromoteImage({ base64, mime })
                      setSelectedR2Key(null)
                    }
                    reader.readAsDataURL(file)
                  }}
                />
              </label>
            )}

            {promoteImage && (
              <div className="mt-2 flex items-start gap-2">
                <img
                  src={`data:${promoteImage.mime};base64,${promoteImage.base64}`}
                  alt="Preview"
                  className={`max-h-20 w-auto rounded border object-contain ${border} ${softBg}`}
                />
                <button
                  onClick={() => { setPromoteImage(null); setSelectedR2Key(null) }}
                  className={`mt-0.5 transition-colors hover:text-destructive ${muted}`}
                  title="Remove image"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        )}

        {saveResult && (
          <div className={`font-mono text-[11px] px-3 py-2 border ${saveResult.ok ? 'bg-success-surface border-success/40 text-success' : 'bg-danger-surface border-destructive/40 text-destructive'}`}>
            {saveResult.msg}
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="font-mono text-[11px] uppercase tracking-widest" onClick={onClose}>
            Close
          </Button>
          <Button
            size="sm"
            className="ml-auto font-mono text-[11px] uppercase tracking-widest gap-1.5"
            disabled={saving || !editHeadline.trim()}
            onClick={isBrand ? handleSaveBrand : handlePromote}
          >
            <Megaphone className="h-3 w-3" />
            {saving
              ? (isBrand ? 'Saving…' : 'Creating…')
              : (isBrand ? 'Save changes' : 'Create campaign')
            }
          </Button>
        </div>
      </div>
    </div>
  )
}
